"use client";

import * as React from "react";
import Image from "next/image";
import Link from "next/link";
import { motion } from "framer-motion";
import { Check, Layers, Database, BarChart, ArrowRight, CheckCircle2, ShieldCheck, Zap, Cpu, Sparkles } from "lucide-react";
import { Card } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";

export default function SolutionsContent() {
  const solutions = [
    {
      id: "saas",
      title: "Custom SaaS Products",
      icon: <Layers className="h-6 w-6 text-primary" />,
      description: "Build secure, multi-tenant cloud software architectures with payment integrations and tenant isolation models.",
      points: [
        "Stripe billing & subscription sync",
        "Tenant schema isolation & data security",
        "Interactive analytics dashboards",
        "Standard OpenAPI developer documentation",
      ],
      outcome: "Launch within 6-12 weeks with full source code hand-off.",
    },
    {
      id: "enterprise",
      title: "Enterprise Systems Integration",
      icon: <Database className="h-6 w-6 text-accent" />,
      description: "Unify fragmented inventory logs, accounting ledger entries, and legacy customer databases into a single, high-speed API.",
      points: [
        "Bidirectional CRM/ERP synchronization",
        "Automated PDF invoice generation triggers",
        "Custom field mappings and synchronization logs",
        "Role-based client permission panels",
      ],
      outcome: "Eliminate manual database entry errors and save 30+ admin hours weekly.",
    },
    {
      id: "bi",
      title: "Business Intelligence Panels",
      icon: <BarChart className="h-6 w-6 text-primary" />,
      description: "Visualize operations bottlenecks, track driver route efficiency, and review billing trends in real-time.",
      points: [
        "Dynamic filterable data charts (React charts)",
        "Automated daily email brief compilation",
        "High-performance SQL query optimizations",
        "Secure cloud data warehousing setups",
      ],
      outcome: "Get real-time operational insights for leadership decision making.",
    },
  ];

  return (
    <div className="w-full bg-[#F8F8F6] text-[#111318] overflow-x-hidden">
      {/* Architectural Hero Section */}
      <section className="relative min-h-[70vh] w-full flex items-center justify-start px-6 sm:px-12 lg:px-20 py-24 overflow-hidden border-b border-[#111318]/10 bg-slate-900">
        <div className="absolute inset-0 z-0">
          <Image
            src="/images/storytelling/office/crm.webp"
            alt="Johnson Softwares Systems Architecture Lab"
            fill
            priority
            sizes="100vw"
            quality={85}
            className="object-cover object-center"
          />
        </div>

        {/* Localized subtle left gradient behind text */}
        <div className="absolute inset-y-0 left-0 w-full sm:w-[65%] lg:w-[50%] bg-gradient-to-r from-white/95 via-white/80 to-transparent pointer-events-none z-[1]" />

        {/* Hero Content */}
        <div className="relative z-10 max-w-2xl text-left">
          <div className="inline-flex items-center gap-2.5 px-3.5 py-1.5 rounded-full bg-white/95 border border-[#111318]/10 shadow-xs text-xs font-mono text-[#111318] mb-6">
            <span className="w-2 h-2 rounded-full bg-[#2563EB] animate-pulse" />
            <span className="tracking-wide font-medium">SYSTEMS ARCHITECTURE // BESPOKE PLATFORMS</span>
          </div>

          <h1 className="text-3xl sm:text-5xl lg:text-6xl font-extrabold text-[#111318] tracking-tight leading-[1.08] mb-6">
            SOFTWARE BUILT LIKE A PRODUCT.
            <br />
            <span className="text-[#2563EB]">NOT A TEMPLATE.</span>
          </h1>

          <p className="text-base sm:text-lg text-[#374151] leading-relaxed mb-8 max-w-xl font-normal">
            We structure custom systems to handle complex multi-tenant billing, high-frequency synchronization, and automated sales and operations pipelines without third-party vendor lock-in.
          </p>

          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3.5 mb-8">
            <Link
              href="/contact"
              className="px-8 py-3.5 rounded-xl bg-[#2563EB] hover:bg-blue-700 text-white font-semibold text-sm transition-all shadow-[0_4px_16px_rgba(37,99,235,0.3)] flex items-center justify-center gap-2 group"
            >
              <span>BOOK SCOPING WORKSHOP</span>
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link
              href="/demos"
              className="px-8 py-3.5 rounded-xl bg-white/95 hover:bg-white border border-[#111318]/15 hover:border-[#2563EB] text-[#111318] hover:text-[#2563EB] font-semibold text-sm transition-all text-center shadow-xs"
            >
              EXPLORE LIVE DEMOS
            </Link>
          </div>

          <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-xs font-mono text-[#374151] pt-4 border-t border-[#111318]/10">
            <span className="flex items-center gap-1.5">
              <ShieldCheck className="w-3.5 h-3.5 text-[#2563EB]" />
              <span>Full Source Code Ownership</span>
            </span>
            <span className="flex items-center gap-1.5">
              <Database className="w-3.5 h-3.5 text-[#2563EB]" />
              <span>Dedicated Tenant Schemas</span>
            </span>
            <span className="flex items-center gap-1.5">
              <Zap className="w-3.5 h-3.5 text-[#2563EB]" />
              <span>Zero Per-Seat SaaS Taxes</span>
            </span>
          </div>
        </div>
      </section>

      {/* Solutions Cards Section */}
      <div className="mx-auto max-w-7xl px-6 sm:px-8 lg:px-12 py-20">
        <div className="space-y-12">
          {solutions.map((sol) => (
            <motion.div
              key={sol.id}
              id={sol.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4 }}
              className="scroll-mt-24 p-8 sm:p-10 rounded-2xl bg-white/95 border border-[#111318]/10 shadow-[0_8px_30px_rgb(0,0,0,0.04)] grid grid-cols-1 lg:grid-cols-12 gap-8 items-stretch hover:border-[#2563EB]/30 transition-all"
            >
              {/* Text Context */}
              <div className="lg:col-span-7 flex flex-col justify-between gap-6">
                <div>
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-2.5 rounded-xl bg-[#2563EB]/10 border border-[#2563EB]/20 text-[#2563EB]">
                      {sol.icon}
                    </div>
                    <h2 className="font-heading font-extrabold text-2xl sm:text-3xl text-[#111318] tracking-tight">
                      {sol.title}
                    </h2>
                  </div>
                  <p className="text-base text-[#5E636B] leading-relaxed mb-6">
                    {sol.description}
                  </p>

                  {/* Checklist */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-4 border-t border-[#111318]/10">
                    {sol.points.map((pt) => (
                      <div key={pt} className="flex items-start gap-2.5 text-xs font-medium text-[#111318]">
                        <CheckCircle2 className="h-4 w-4 text-[#2563EB] shrink-0 mt-0.5" />
                        <span>{pt}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <Link
                    href="/contact"
                    className="inline-flex items-center gap-2 text-xs font-bold text-[#2563EB] hover:text-blue-700 group transition-colors"
                  >
                    <span>Request technical architecture blueprint</span>
                    <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
                  </Link>
                </div>
              </div>

              {/* Outcome & CTA */}
              <div className="lg:col-span-5 bg-[#F8F8F6] p-6 sm:p-8 rounded-xl border border-[#111318]/5 flex flex-col justify-between">
                <div>
                  <span className="text-[10px] font-mono uppercase tracking-widest text-[#5E636B] block mb-2 font-bold">
                    TARGET BUSINESS OUTCOME
                  </span>
                  <p className="text-base sm:text-lg font-bold text-[#111318] leading-relaxed mb-6">
                    {sol.outcome}
                  </p>
                </div>
                <div className="flex flex-col gap-3 pt-4 border-t border-[#111318]/10">
                  <Link 
                    href={sol.id === "saas" ? "/solutions/saas" : "/contact"} 
                    className="w-full py-3.5 px-6 rounded-xl bg-[#2563EB] hover:bg-blue-700 text-white font-semibold text-xs text-center transition-all shadow-[0_4px_16px_rgba(37,99,235,0.25)] flex items-center justify-center gap-2"
                  >
                    <span>{sol.id === "saas" ? "Explore TalentFlow AI Platform" : "Book Scoping Workshop"}</span>
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                  {sol.id === "saas" && (
                    <Link
                      href="/contact"
                      className="w-full py-3 px-6 rounded-xl bg-white hover:bg-slate-50 border border-[#111318]/15 text-[#111318] font-semibold text-xs text-center transition-all"
                    >
                      Schedule Custom SaaS Scoping
                    </Link>
                  )}
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Boardroom Consultation Prompt */}
        <div className="mt-24 p-8 sm:p-12 rounded-2xl bg-white/95 border border-[#111318]/10 shadow-[0_8px_30px_rgb(0,0,0,0.04)] text-center max-w-4xl mx-auto">
          <span className="text-xs font-mono tracking-widest text-[#2563EB] uppercase block mb-2 font-semibold">
            DISCOVERY &amp; LOGIC AUDIT
          </span>
          <h3 className="font-heading font-extrabold text-2xl sm:text-3xl text-[#111318] mb-3 tracking-tight">
            Have a custom operational requirement?
          </h3>
          <p className="text-sm text-[#5E636B] mb-8 max-w-xl mx-auto leading-relaxed">
            Our principal engineers help you audit operational bottlenecks and scope bespoke software architectures with zero per-seat SaaS taxes.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3.5">
            <Link
              href="/contact"
              className="w-full sm:w-auto px-8 py-3.5 rounded-xl bg-[#2563EB] hover:bg-blue-700 text-white font-semibold text-sm transition-all shadow-[0_4px_16px_rgba(37,99,235,0.3)]"
            >
              Book Free Scoping Workshop
            </Link>
            <a
              href="https://wa.me/919880893416?text=Hi%20Johnson%20Softwares,%20I'd%20like%20to%20consult%20on%20an%20enterprise%20software%20solution."
              target="_blank"
              rel="noopener noreferrer"
              className="w-full sm:w-auto px-8 py-3.5 rounded-xl bg-[#F8F8F6] hover:bg-white border border-[#111318]/15 text-[#111318] font-semibold text-sm transition-all"
            >
              Chat on WhatsApp
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
